import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import Icon from 'react-web-vector-icons';
import Toaster from "../../../components/Toaster";
import PageBase from "../../../components/PageBase";
import Datatable from "../../../components/Datatable";
import * as databaseBackupActions from "../../../redux/DatabaseBackups/actions";
import LoadingPanel from "../../../components/LoadingPanel";
import columns from './column.js';
import PopupWin from "../../../components/PopupWin";
import PopupButton from "../../../components/PopupButton";
import CreateSnapshot from "./CreateSnapshot";
import Button from "../../../components/Button";
import {showConfirm} from "../../../components/ConfirmBox";
import LoadData from "./LoadData";
import useColumnFilters from '../../../hooks/useColumnFilters.js';

const DBSnapshots = props => {
	const [showDataLoader, setShowDataLoader] = useState(false);
	const [showBackupCreationPopup, setShowBackupCreationPopup] = useState(false);
	const [loading, setLoading] = useState(true);
	const [createSnapshotFunc, setCreateSnapshotFunc] = useState(null);
	const dispatch = useDispatch();
	const snapshots = useSelector(state => state.databaseBackups.snapshots);

    const renderSectionTitle = (title, count) => {
        const suffix = count > 0 ? ` (${count})` : '';
        return (
            <div style={{fontSize: '18px', fontWeight: 'bold', marginBottom: '20px'}}>
                {title}{suffix}
            </div>
        );
    };

	const {
		filters,
		sortBy,
		sortOrder,
		filteredData,
		handleFilterChange,
		resetWith,
		updateData,
		uniqueValuesByColumn,
		filterOrder
	} = useColumnFilters({
		data: snapshots || [],
		columns,
		storageKey: 'db_snapshots',
		initialState: {}
	});

	const checkStatus = () => {
		setTimeout(() => {
			dispatch(databaseBackupActions.loadSnapshots(checkStatus));
		}, 10000);
	};

	const refreshData = async () => {
		setLoading(true);
		await dispatch(databaseBackupActions.loadSnapshots(checkStatus));
		setLoading(false);
	};

	useEffect(() => {
		const load = async () => {
			await dispatch(databaseBackupActions.loadSnapshots(checkStatus));
			setLoading(false);
		};
		load();
	}, []);

	useEffect(() => {
		updateData(snapshots);
	}, [snapshots]);

	if(loading) {
		return (
			<PageBase title="Database Management" removeMargins>
				<LoadingPanel />
			</PageBase>
		)
	}

	const afterRestore = (result) => {
		if(result.status === "failed") {
			if(result.message) {
				Toaster.showWarn(result.message);
			}
			else {
				Toaster.showWarn("Restore failed. The database is probably busy working on another task. Try again later.");
			}
		}
		else {
			Toaster.showSuccess("Action is in progress.");
			refreshData();
		}
	};

	const afterBackupCreation = (status) => {
		setShowBackupCreationPopup(false);

		if(status === "failed") {
			Toaster.showWarn("Backup creation failed. The database is probably busy working on another task. Try again later.");
		}
		else {
			Toaster.showSuccess("Backup creation is in progress.");
			refreshData();
		}
	};

	const renderActions = (row) => {
		if(row.type === "Live") {
			return <div/>
		}
		else if(row.type === "Sandbox") {
			return (
				<div style={{display: 'grid', gridTemplateColumns: '70px 60px 70px', columnGap: '20px'}}>
					<div
						onClick={() => switchLiveDatabase(row)}
						style={{color: 'blue', textDecoration: 'underline', cursor: 'pointer'}}>
						Make Live
					</div>
					<div
						onClick={() => removeSnapshot(row)}
						style={{color: 'blue', textDecoration: 'underline', cursor: 'pointer'}}>
						Remove
					</div>
					<div
						onClick={() => setShowDataLoader(true)}
						style={{color: 'blue', textDecoration: 'underline', cursor: 'pointer'}}>
						Load Data
					</div>
				</div>
			);
		}

		return (
			<div style={{display: 'grid', gridTemplateColumns: '70px 100px 100px', columnGap: '20px'}}>
				<div
					onClick={() => switchLiveDatabase(row)}
					style={{color: 'blue', textDecoration: 'underline', cursor: 'pointer'}}>
					Make Live
				</div>
				{
					row.type === "Snapshot" ?
						<div
							onClick={() => snapshotToSandbox(row)}
							style={{color: 'blue', textDecoration: 'underline', cursor: 'pointer'}}>
							Make Sandbox
						</div>
						:
						<></>
				}
				<div
					onClick={() => removeSnapshot(row)}
					style={{color: 'blue', textDecoration: 'underline', cursor: 'pointer'}}>
					Remove
				</div>
			</div>
		);
	};

	const index = columns.findIndex(c => c.id === 'actions');
	columns[index].cell = renderActions;

	const allStatusesAvailable = () => {
		return snapshots.filter(s => s.status !== 'available').length === 0;
	};

	const removeSnapshot = async (row) => {
		if(!allStatusesAvailable()) {
			Toaster.showWarn("Further actions are only possible once all elements are in 'available' status");
			return;
		}

		showConfirm({
			title: `Remove ${row.type}`,
			message: `${row.type} <b>${row.name}</b> will be removed.<br/><br/>Would you like to continue?`,
			onConfirm: () => onConfirmRemove(row),
		})
	};

	const onConfirmRemove = async (row) => {
		if(row.type === "Snapshot") {
			setLoading(true);
			await dispatch(databaseBackupActions.removeSnapshot(row.name, row.type, false, afterRemove));
		}
		else {
			showConfirm({
				title: `Remove Sandbox`,
				message: "Would you like to save a final backup before deleting the sandbox database?",
				cancelButtonLabel: 'No',
				cancelButtonType: 'danger',
				onConfirm: async () => {
					setLoading(true);
					await dispatch(databaseBackupActions.removeSnapshot(row.name, row.type, true, afterRemove));
				},
				onCancel: async () => {
					setLoading(true);
					await dispatch(databaseBackupActions.removeSnapshot(row.name, row.type, false, afterRemove));
				}
			})
		}
	}

	const afterRemove = (result) => {
		setLoading(false);

		if(result === "failed") {
			Toaster.showWarn("Cannot delete backup. Try again later.");
		}
		else {
			Toaster.showSuccess("Backup deleted!");
		}
	};

	const switchLiveDatabase = async (row) => {
		if(!allStatusesAvailable()) {
			Toaster.showWarn("Further actions are only possible once all elements are in 'available' status");
			return;
		}

		showConfirm({
			title: `Change Live Database`,
			message: `<b>${row.name}</b> will become the live database.<br/><br/>Would you like to continue?`,
			onConfirm: async () => {
				setLoading(true);
				await dispatch(databaseBackupActions.switchLiveDatabase(row.name, row.type, afterRestore));
				setLoading(false);
			}
		})
	};

	const snapshotToSandbox = async (row) => {
		if(!allStatusesAvailable()) {
			Toaster.showWarn("Further actions are only possible once all elements are in 'available' status");
			return;
		}

		showConfirm({
			title: `Create Sandbox`,
			message: `<b>${row.name}</b> will become the sandbox database.<br/><br/>Would you like to continue?`,
			onConfirm: async () => {
				setLoading(true);
				await dispatch(databaseBackupActions.snapshotToSandbox(row.name, afterRestore));
				setLoading(false);
			}
		})
	};

	const onCreateSnapshot = () => {
		showConfirm({
			title: `Create Snapshot`,
			message: `A new snapshot of the Live database will be created.<br/><br/>Would you like to continue?`,
			onConfirm: handleCreateBackup
		})
	};

	const handleCreateBackup = () => {
		if(!allStatusesAvailable()) {
			Toaster.showWarn("Further actions are only possible once all elements are in 'available' status");
			return;
		}

		setShowBackupCreationPopup(true);
	};

	return (
		<PageBase title="Database Management" removeMargins>
			<PopupWin
				closeOnEscape={false}
				closeOnDocumentClick={false}
				visible={showBackupCreationPopup}
				onClose={() => setShowBackupCreationPopup(false)}
				title="Create New Database Backup"
				popupStyle={{width: '550px', height: '400px', overflow: 'none'}}
				footer={
					<>
						<PopupButton
							variant="secondary"
							onClick={() => setShowBackupCreationPopup(false)}
						>
							Cancel
						</PopupButton>
						<PopupButton
							variant="primary"
							onClick={createSnapshotFunc}
							disabled={!createSnapshotFunc}
						>
							Create Backup
						</PopupButton>
					</>
				}>
				<CreateSnapshot
					onClose={afterBackupCreation}
					onCreateSnapshot={(func) => setCreateSnapshotFunc(() => func)}
				/>
			</PopupWin>

			{
				showDataLoader ?
					<LoadData sandbox={filteredData.find(f => f.type === 'Sandbox')} onCancel={() => setShowDataLoader(false)} />
					:
					<>
						<div style={{margin: '20px 0', display: 'flex', alignItems: 'center', marginLeft: '30px' }}>
							<Button leftFontIcon={<Icon name="pluscircleo" font="AntDesign" color="white" size={17}/>} onClick={onCreateSnapshot} type="blue">&nbsp;&nbsp;Create Snapshot</Button>
						</div>

						<div style={{margin: '0 30px'}}>
							{renderSectionTitle('Databases', filteredData.filter(f => f.type !== 'Snapshot').length)}
							<Datatable 
								gridHeight="100px"
								tableName="db_snapshots"
								containerClassName="dbsnapshots-grid"
								columns={columns}
								sortBy={sortBy}
								sortOrder={sortOrder}
								data={filteredData.filter(f => f.type !== 'Snapshot')}
								filters={filters}
								onFilterChange={handleFilterChange}
								filterOrder={filterOrder}
								uniqueValuesByColumn={uniqueValuesByColumn}
								allData={snapshots}
							/>

							<div style={{marginTop: '20px'}}>
									{renderSectionTitle('Snapshots', filteredData.filter(f => f.type === 'Snapshot').length)}
							</div>
							<Datatable 
								gridHeight="calc(100vh - 550px)"
								tableName="db_snapshots_history"
								containerClassName="dbsnapshots-grid"
								columns={columns}
								sortBy={sortBy}
								sortOrder={sortOrder}
								data={filteredData.filter(f => f.type === 'Snapshot')}
								filters={filters}
								onFilterChange={handleFilterChange}
								filterOrder={filterOrder}
								uniqueValuesByColumn={uniqueValuesByColumn}
								allData={snapshots}
							/>
						</div>
					</>
			}
		</PageBase>
	)
};

export default DBSnapshots;
